import { Injectable } from '@angular/core';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import { catchError, concatMap, filter, map, take, tap} from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { IDefinition } from '../interfaces/definition.interface';
import {forkJoin, of, ReplaySubject, Subject, throwError} from "rxjs";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import { cloneDeep, isEmpty } from 'lodash';

@Injectable()
export class ConfigurationService {
  static readonly URL = '/configuration/';
  private resources$: Map<string, ReplaySubject<IDefinition>> = new Map();
  private queryEmitter: Subject<{ type, id }> = new Subject();
  private configMap = new Map<string, any>();

  constructor(private _http: HttpInterceptorService, private _ispmHttpClient: IspmHttpClient ) {
    this.registerQueryEmitter();
  }

  /**
   * resolve configuration by id
   * @param {string} id
   */
  getConfiguration(id: string);
  /**
   * @inheritDoc
   * @param {string} id
   * @param {string} type
   */
  getConfiguration(id: string, type: string);
  getConfiguration(id: string, type: string = 'test'): Observable<IDefinition> {
    return this._http.get(`${ConfigurationService.URL}definition/${type}${id}/`).pipe(map(res => res.json()));
  }

  getConfigurationByContext(type: string): Observable<any> {
    if (this.configMap.has(type)) {
      return of(cloneDeep(this.configMap.get(type)));
    } else {
      let body = {
        resource: 'EditorConfiguration',
        queries: [{ context: type }]
      };

      return this._ispmHttpClient.post('definition/EditorConfiguration/Bundle', body).pipe(
        tap((data: any) => {
          if (!isEmpty(data)) {
            this.configMap.set(type, cloneDeep(data));
          }
        }),
        catchError((error) => {
          console.error(`Failed to fetch configuration for context: ${type}`, error);
          return throwError(`Failed to fetch configuration for context: ${type}`);
        })
      );
    }
  }

  clearCache(type?: string): void {
    if (type) {
      this.configMap.delete(type);
    } else {
      this.configMap.clear();
    }
  }

  registerForConfiguration(id: string, type: string): Observable<IDefinition> {
    let resource;
    if (this.resources$.has(id)) {
      resource = this.resources$.get(id);
    } else {
      resource = this.updateConfigurationFetchQuery(id, type);
    }
    return resource.asObservable().pipe(take(1));
  }

  updateConfigurationFetchQuery(id: string, type: string) {
    const subject = new ReplaySubject<IDefinition>(1);
    this.resources$.set(id, subject);
    this.queryEmitter.next({type, id});
    return subject;
  }

  clearCachedConfigurations() {
    Array.from(this.resources$.values()).forEach(val => val.complete());
    this.resources$.clear();
  }


  private registerQueryEmitter() {
    this.queryEmitter.asObservable().pipe(
      filter(items => !!items),
      map(items => (Array.isArray(items))? items : [items]),
      concatMap(items =>
        forkJoin([
          this._http.post(`${ConfigurationService.URL}definition/bundle`, {items}),
          of(items[0])
        ])
      ),
      map(([response, item]) => ([response.json(), item])),
    ).subscribe(([resources, item]: [any[], {type: string, id: string}]) => {
            if (resources.length) {
              resources.forEach(resource => {
                if (this.resources$.has(resource.id)){
                  this.resources$.get(resource.id).next(resource);
                }
              })
            } else {
              this.resources$.get(item.id).next(null);
            }
    })
  }
}
